import { useQuery } from "@apollo/client";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import {
  BulkDownloadSubmissionPreviewQuery,
  BulkDownloadSubmissionPreviewQueryVariables,
} from "generated-graphql/graphql";
import { useExportData } from "hooks/useExportData";
import { BULK_DOWNLOAD_SUBMISSION_PREVIEW_QUERY } from "queries/submissions";
import { useState } from "react";

type Props = {
  open: boolean;
  onClose: () => void;
  search: string;
  tenantId: string | undefined;
};

export function BulkDownloadSubmissionsModal({ open, onClose, search, tenantId }: Props) {
  const [includeAttachments, setIncludeAttachments] = useState(false);

  const {
    data,
    loading: previewLoading,
    error: previewError,
  } = useQuery<BulkDownloadSubmissionPreviewQuery, BulkDownloadSubmissionPreviewQueryVariables>(
    BULK_DOWNLOAD_SUBMISSION_PREVIEW_QUERY,
    {
      variables: { search, includeAttachments },
      skip: !open,
      fetchPolicy: "network-only",
    }
  );

  const { startExport, status } = useExportData({ autoDownload: true, disableCache: true });
  const preparing = status === "loading";

  const submissionCount = data?.bulkDownloadSubmissionPreview.submissionCount ?? 0;
  const hasNone = !previewLoading && previewError == null && submissionCount === 0;

  let countMessage: string;
  if (previewLoading) {
    countMessage = "Counting matching submissions…";
  } else if (previewError != null) {
    countMessage = "Couldn't count matching submissions. Please try again.";
  } else {
    countMessage = `${submissionCount} filed submissions will be included.`;
  }

  // The download runs as a background job this modal polls; closing it (X,
  // backdrop click, or Escape) unmounts the poll and silently drops the
  // download, so block every close path while it's preparing.
  const handleClose = () => {
    if (preparing) return;
    onClose();
  };

  const handleDownload = () =>
    startExport(
      { exportType: "submissionPdfs", tenantId, filter: { search, includeAttachments } },
      onClose
    );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Download submission PDFs"
      primaryActionButtonProps={{
        onClick: handleDownload,
        loading: preparing,
        disabled: previewLoading || hasNone || preparing || previewError != null,
        saveText: "Download",
      }}
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        Downloads the filed PDF for each completed submission matching your current filters — the
        signed copy when there is one. Folders are organized by state.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
        {preparing ? "Preparing your download — large selections can take a minute…" : countMessage}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={includeAttachments}
            onChange={(event) => setIncludeAttachments(event.target.checked)}
          />
        }
        label="Include attachments"
      />
    </Dialog>
  );
}
